import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { sendSupportMessage, fetchSupportMessages, addMessage, deleteSupportSession, fetchCannedResponses } from '../../store/Slices/supportSlice';
import { Box, Typography, AppBar, Toolbar, IconButton, Button, Snackbar } from '@mui/material';
import ChatBox from './ChatBox';
import socket from '../../services/socket';
import { lightTheme, darkTheme } from '../../themes/themes';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ChatSessionPage = () => {
  const { sessionId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.data);
  const { messages, sessions, cannedResponses } = useSelector((state) => state.support);
  const isLightTheme = useSelector((state) => state.theme.light);
  const theme = isLightTheme ? lightTheme : darkTheme;
  const currentSession = sessions.find((s) => s._id === sessionId);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    if (user?._id && sessionId) {
      dispatch(fetchSupportMessages(sessionId));
      dispatch(fetchCannedResponses()); // جلب الردود المعلبة
      socket.emit('join', { user_id: user._id });
      socket.emit('join_session', { session_id: sessionId });

      socket.on('message', (message) => {
        if (message.support_session_id === sessionId) {
          console.log(`Received message for session ${sessionId}:`, message);
          dispatch(addMessage(message));
        }
      });

      socket.on('session_ended', ({ support_session_id }) => {
        if (support_session_id === sessionId) {
          console.log(`Session ${sessionId} ended`);
          setSnackbarMessage('Chat has been ended');
          setSnackbarOpen(true);
          setTimeout(() => {
            navigate(user.role === 'Support' ? '/staff/support-dashboard' : '/support/sessions');
          }, 2000);
        }
      });

      return () => {
        socket.off('message');
        socket.off('session_ended');
        socket.emit('leave_session', { session_id: sessionId });
      };
    }
  }, [dispatch, user?._id, sessionId, navigate]);

  const handleSendMessage = (sessionId, message, isCanned = false, cannedResponseId = null) => {
    if (!message.trim() || !user?._id) return;

    const messageData = {
      sessionId,
      message,
      senderId: user._id,
      cannedResponseId: isCanned ? cannedResponseId : null,
    };

    dispatch(sendSupportMessage(messageData));
    socket.emit('message', {
      support_session_id: sessionId,
      message,
      sender_id: user._id,
      is_canned: isCanned,
    });
  };

  const handleEndSession = () => {
    if (window.confirm('Are you sure you want to end this session?')) {
      dispatch(deleteSupportSession({ sessionId, ender_id: user._id }))
        .unwrap()
        .then(() => {
          socket.emit('end_session', { support_session_id: sessionId });
          setSnackbarMessage('Chat has been ended');
          setSnackbarOpen(true);
          setTimeout(() => {
            navigate(user.role === 'Support' ? '/staff/support-dashboard' : '/support/sessions');
          }, 2000);
        })
        .catch((err) => console.error('Error ending session:', err));
    }
  };

  const handleBackClick = () => {
    navigate(user.role === 'Support' ? '/staff/support-dashboard' : '/support/sessions');
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (!currentSession) {
    return (
      <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh', p: 3 }}>
        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
          Session not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
      <AppBar
        position="static"
        sx={{
          bgcolor: theme.palette.background.paper,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleBackClick}
              sx={{
                mr: 2,
                color: theme.palette.brand.main,
                '&:hover': { color: '#d4a027' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 'bold',
              }}
            >
              {currentSession.support_request_id?.subject || 'Support Session'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            onClick={handleEndSession}
            sx={{
              borderRadius: '6px',
              fontSize: '0.75rem',
              px: 2,
              py: 0.5,
              borderColor: theme.palette.error.main,
              color: theme.palette.error.main,
              '&:hover': {
                borderColor: theme.palette.error.dark,
                color: theme.palette.error.dark,
              },
            }}
          >
            End Chat
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto', height: '100%' }}>
        <ChatBox
          sessionId={sessionId}
          messages={messages.filter((msg) => msg.support_session_id === sessionId)}
          sendMessage={handleSendMessage}
          userId={user._id}
          isSupportStaff={currentSession.support_staff_id === user._id}
          cannedResponses={cannedResponses} // تمرير الردود المعلبة
        />
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        sx={{ '& .MuiSnackbarContent-root': { bgcolor: theme.palette.brand.main, color: '#fff' } }}
      />
    </Box>
  );
};

export default ChatSessionPage;